// @catalog F.006
// @catalog F.030
// RankingBars — ranking horizontal con barra proporcional, delta pill,
// y tag opcional. Click en fila dispara onSelect (abre DrillPanel).
import type { ReactNode } from "react";
import { Box } from "@mui/material";

export interface RankingItem<TRaw = unknown> {
  id: string | number;
  name: string;
  value: number;
  // Delta porcentual vs comparativo (LY/prev). null/undefined oculta el pill.
  delta?: number | null;
  // Tag opcional (ej. "Atención", "Top", "Bajo").
  tag?: string | null;
  // Estilo del tag.
  tagStyle?: "alert" | "warn" | "good" | "neutral";
  // Data original para pasar al DrillPanel.
  raw?: TRaw;
}

interface RankingBarsProps<TRaw = unknown> {
  items: ReadonlyArray<RankingItem<TRaw>>;
  formatValue: (v: number) => string;
  onSelect?: (item: RankingItem<TRaw>) => void;
  // Umbral bajo el cual el delta se considera "low" (rojo). Default -8.
  lowThreshold?: number;
  // Render alterno del valor (ej. para mostrar "%" además del valor base).
  renderValue?: (item: RankingItem<TRaw>) => ReactNode;
  emptyMessage?: string;
}

function tagStyleVars(style: RankingItem["tagStyle"]): {
  bg: string;
  color: string;
} {
  if (style === "alert") return { bg: "var(--neg-soft)", color: "var(--neg)" };
  if (style === "warn") return { bg: "var(--warn-soft)", color: "var(--warn)" };
  if (style === "good") return { bg: "var(--pos-soft)", color: "var(--pos)" };
  return { bg: "var(--bg-2)", color: "var(--ink-2)" };
}

export function RankingBars<TRaw = unknown>(
  props: RankingBarsProps<TRaw>,
): JSX.Element {
  const {
    items,
    formatValue,
    onSelect,
    lowThreshold = -8,
    renderValue,
    emptyMessage = "Sin datos",
  } = props;

  if (!items || items.length === 0) {
    return (
      <Box sx={{ color: "var(--ink-3)", padding: "12px", fontSize: "13px" }}>
        {emptyMessage}
      </Box>
    );
  }

  const max = Math.max(...items.map((it) => it.value), 1);

  return (
    <Box>
      {items.map((it, idx) => {
        const pct = (it.value / max) * 100;
        const isLow = it.delta != null && it.delta < lowThreshold;
        const isWarn =
          it.delta != null && it.delta < 0 && it.delta >= lowThreshold;
        const fillColor = isLow
          ? "var(--neg)"
          : isWarn
            ? "var(--warn)"
            : "var(--accent)";
        const tagPalette = tagStyleVars(it.tagStyle);
        const deltaPalette =
          it.delta == null
            ? { color: "var(--ink-3)", bg: "transparent" }
            : it.delta >= 0
              ? { color: "var(--pos)", bg: "var(--pos-soft)" }
              : isLow
                ? { color: "var(--neg)", bg: "var(--neg-soft)" }
                : { color: "var(--warn)", bg: "var(--warn-soft)" };

        return (
          <Box
            key={it.id}
            role={onSelect ? "button" : undefined}
            tabIndex={onSelect ? 0 : undefined}
            onClick={() => onSelect?.(it)}
            onKeyDown={(e) => {
              if (onSelect && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                onSelect(it);
              }
            }}
            sx={{
              display: "grid",
              gridTemplateColumns: "18px 1fr auto",
              alignItems: "center",
              gap: "12px",
              padding: "var(--pad-row) 0",
              borderBottom: "1px solid var(--border)",
              fontSize: "13px",
              cursor: onSelect ? "pointer" : "default",
              "&:last-child": { borderBottom: 0 },
              "&:hover": onSelect
                ? { backgroundColor: "var(--bg)" }
                : undefined,
              "&:focus-visible": onSelect
                ? {
                    outline: "2px solid var(--accent)",
                    outlineOffset: "-2px",
                  }
                : undefined,
            }}
          >
            <Box
              component="span"
              sx={{
                fontSize: "11px",
                color: "var(--ink-4)",
                fontVariantNumeric: "tabular-nums",
                textAlign: "right",
              }}
            >
              {idx + 1}
            </Box>
            <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: "4px" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <Box
                  component="span"
                  sx={{
                    fontWeight: 500,
                    color: "var(--ink-1)",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {it.name}
                </Box>
                {it.tag && (
                  <Box
                    component="span"
                    sx={{
                      padding: "2px 7px",
                      borderRadius: "4px",
                      fontSize: "11px",
                      backgroundColor: tagPalette.bg,
                      color: tagPalette.color,
                      fontWeight: 500,
                      lineHeight: "16px",
                    }}
                  >
                    {it.tag}
                  </Box>
                )}
              </Box>
              <Box
                sx={{
                  position: "relative",
                  height: "6px",
                  backgroundColor: "var(--bg-2)",
                  borderRadius: "3px",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: `${pct}%`,
                    backgroundColor: fillColor,
                    borderRadius: "3px",
                  }}
                />
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              <Box
                component="span"
                sx={{ fontWeight: 500, color: "var(--ink-1)", fontSize: "13px" }}
              >
                {renderValue ? renderValue(it) : formatValue(it.value)}
              </Box>
              {it.delta != null && (
                <Box
                  component="span"
                  sx={{
                    padding: "2px 6px",
                    borderRadius: "4px",
                    fontSize: "11.5px",
                    fontWeight: 600,
                    minWidth: "56px",
                    textAlign: "right",
                    color: deltaPalette.color,
                    backgroundColor: deltaPalette.bg,
                  }}
                >
                  {it.delta >= 0 ? "+" : ""}
                  {it.delta.toFixed(1)}%
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
